import {
  IonBackButton, IonButtons, IonContent, IonHeader, IonItem, IonItemDivider, IonLabel, IonList, IonNote, IonPage, IonSpinner, IonTitle, IonToolbar,
} from '@ionic/react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import { getSupabase } from '../../lib/supabase.js';

type VerificationRecord = {
  event_id: string; operational_date: string; registered_at: string; server_rev: number; operator_name: string;
  operator_signature_fingerprint: string | null; self_hash: string | null; activity: string; equipment_ids: string[];
};
type VerificationData = { records: VerificationRecord[] };

function period(search: string): { year: number; month: number; tenantId: string | null } {
  const query = new URLSearchParams(search);
  const now = new Date();
  const year = Number(query.get('year'));
  const month = Number(query.get('month'));
  return { year: Number.isInteger(year) && year >= 2020 ? year : now.getFullYear(), month: Number.isInteger(month) && month >= 1 && month <= 12 ? month : now.getMonth() + 1, tenantId: query.get('tenant') };
}

export function CleaningRegisterVerificationPage(): JSX.Element {
  const { profile } = useAuth();
  const location = useLocation();
  const { year, month, tenantId: requestedTenantId } = period(location.search);
  const tenantId = requestedTenantId ?? profile?.active_tenant_id ?? null;
  const { data, isLoading, error } = useQuery({
    queryKey: ['verify-cleaning-month', tenantId, year, month], enabled: !!tenantId,
    queryFn: async (): Promise<VerificationData> => {
      const { data, error } = await getSupabase().functions.invoke<VerificationData>('verify-cleaning-month', {
        body: { tenant_id: tenantId, year, month },
      });
      if (error) throw error;
      return data ?? { records: [] };
    },
  });
  const byOperator = new Map<string, VerificationRecord[]>();
  for (const record of data?.records ?? []) {
    const operatorKey = record.operator_signature_fingerprint ?? `legacy:${record.operator_name}`;
    const rows = byOperator.get(operatorKey) ?? [];
    rows.push(record);
    byOperator.set(operatorKey, rows);
  }
  return <IonPage>
    <IonHeader><IonToolbar><IonButtons slot="start"><IonBackButton defaultHref="/haccp/registri" /></IonButtons><IonTitle>Verifica registro pulizie</IonTitle></IonToolbar></IonHeader>
    <IonContent className="ion-padding">
      <p style={{ fontSize: 13, color: '#64748b', marginTop: 0 }}>
        Elenco read-only delle attività incluse nel MOD 09 {String(month).padStart(2, '0')}/{year}. L’ID firma identifica stabilmente l’account; ogni movimento conserva il proprio hash HACCP originale.
      </p>
      {isLoading && <IonSpinner name="dots" />}
      {error && <p style={{ color: '#b91c1c' }}>Impossibile caricare la verifica del registro.</p>}
      {!isLoading && !error && <IonList inset>
        {[...byOperator.entries()].map(([operatorKey, records]) => <div key={operatorKey}>
          <IonItemDivider><IonLabel>Firma operatore · {records[0]?.operator_name}</IonLabel></IonItemDivider>
          <IonItem lines="full">
            <IonLabel>
              <p>ID firma TrackEAT</p>
              <p style={{ fontFamily: 'monospace', fontSize: 11, overflowWrap: 'anywhere' }}>{records[0]?.operator_signature_fingerprint ?? 'Identità non disponibile per questo account storico.'}</p>
            </IonLabel>
          </IonItem>
          {records.map((record) => <IonItem key={record.event_id} lines="full">
            <IonLabel>
              <h2>{record.operational_date} · {record.activity}</h2>
              <p>Registrato alle {new Date(record.registered_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })} · revisione evento {record.server_rev}</p>
              <p style={{ fontFamily: 'monospace', fontSize: 11, overflowWrap: 'anywhere' }}>{record.self_hash ?? 'Hash non disponibile per questo evento storico.'}</p>
            </IonLabel>
          </IonItem>)}
        </div>)}
        {(data?.records ?? []).length === 0 && <IonItem><IonLabel>Nessuna pulizia registrata nel periodo selezionato.</IonLabel></IonItem>}
      </IonList>}
      <IonNote>L’ID firma è un’impronta pubblica dell’account autenticato, non una chiave privata né una firma digitale qualificata. La pagina non modifica eventi, operatori o catena di integrità.</IonNote>
    </IonContent>
  </IonPage>;
}
